<script setup lang="ts">

import {nextTick, onMounted, Ref, ref, watch, computed} from "vue";
import Multiselect from "vue-multiselect";
import BtnTab from "../Utils/BtnTab.vue";
import Week from "../Models/Week";
import SanctuaryCompany from "./Models/SanctuaryCompany";
import SanctuarySession from "./Models/SanctuarySession";
import SanctuaryEstablishment from "./Models/SanctuaryEstablishment";
import PlanningTabSanctuary from "./Utils/PlanningTabSanctuary.vue";
import LogoSanctuary from "./Utils/LogoSanctuary.vue";
import { useSportSessions } from '@/composables/useSportSessions';

const { sessions: sportSessions, loadSessions } = useSportSessions();

const mySessions = computed(() => {
    if (!sportSessions.value) return [];
    return [...sportSessions.value.upcoming, ...sportSessions.value.past];
});

const refs = ref<{ weeks: Week[], companies: SanctuaryCompany[]}>({
    weeks: [],
    companies: [],
});

const search = ref<{
    week: Week|null,
    establishments: SanctuaryEstablishment[],
}>({
    week: null,
    establishments: [],
})

const planning = ref<{[key: string]: SanctuarySession[]}>({});
const planningLoading = ref(false);

onMounted(() => {
    loadSessions();
    fetch('/sanctuary/refs/load')
        .then(response => response.json())
        .then(data => {
            refs.value.weeks = data.weeks;
            refs.value.companies = data.companies.map(company => {
                company.establishments = company.establishments.map(establishment => {
                    establishment.company = company;
                    establishment.fullTitle = company.name + ' : ' + establishment.title;

                    return establishment;
                })

                return company;
            });

            search.value.week = refs.value.weeks[0];
            search.value.establishments = refs.value.companies.reduce((establishmentList: SanctuaryEstablishment[], company: SanctuaryCompany) => {
                return establishmentList.concat(company.establishments)
            }, []).filter(establishment => {
                return establishment.id === 8709 || establishment.id === 7410 || establishment.id === 8704;
            })
        })
})

watch(search, function() {
    planningLoading.value = true;
    planning.value = {};

    fetch('/sanctuary/planning', {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            dates: Object.values(search.value.week?.days),
            establishments: search.value.establishments.map(establishment => establishment.id),
        })
    })
        .then(response => response.json())
        .then(data => {
            const activeTab = document.querySelector('#tab-planning button.active');
            planning.value = data;
            planningLoading.value = false;

            let activeDay: null|string = null
            if (Object.keys(planning.value).length > 0) {
                if (activeTab) {
                    activeDay = activeTab.getAttribute('id').split('-')[0];
                    if (planning.value[search.value.week.days[activeDay]] === undefined) {
                        activeDay = null;
                    }
                }
            }

            if (activeDay === null) {
                const days = Object.keys(search.value.week.days);
                const dates = Object.values(search.value.week.days);
                const planningDates = Object.keys(planning.value);
                if (planningDates.length > 0) {
                    activeDay = days[dates.indexOf(planningDates[0])];
                }
            }
            if (activeDay) {
                nextTick(() => {
                    let button = document.querySelector('#'+activeDay+'-tab');
                    button.classList.remove("active")
                    bootstrap.Tab.getOrCreateInstance(button).show()
                });
            }
        })
}, {deep: true});

const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return parts[2] + '/' + parts[1];
};

</script>

<template>
<div>
    <header>
        <div class="container p-4">
            <logo-sanctuary />
        </div>
    </header>


    <main class="container-fluid mt-3">
        <div>
            <form @submit="e => e.preventDefault()">
                <div class="row">
                    <div class="col-lg-3 mb-3">
                        <multiselect :options="refs.weeks" label="label" :multiple="false" v-model="search.week" />
                    </div>
                    <div class="col-lg-6  mb-3">
                        <multiselect :options="refs.companies" track-by="id" multiple v-model="search.establishments" group-values="establishments" group-label="name" label="fullTitle">
                            <template v-slot:option="{ option }">
                                <span>{{ option.$groupLabel || option.title }}</span>
                            </template>
                        </multiselect>
                    </div>
                </div>
            </form>
        </div>


        <div v-if="search.week">
            <ul class="nav nav-tabs nav-fill" id="tab-planning" role="tablist">
                <btn-tab tab-id="monday" key="monday" :label="'Lundi ' + formatDate(search.week.days['monday'])" :disabled="planning[search.week.days['monday']] === undefined"/>
                <btn-tab tab-id="tuesday" key="tuesday" :label="'Mardi ' + formatDate(search.week.days['tuesday'])"  :disabled="planning[search.week.days['tuesday']] === undefined"/>
                <btn-tab tab-id="wednesday" key="wednesday" :label="'Mercredi ' + formatDate(search.week.days['wednesday'])"  :disabled="planning[search.week.days['wednesday']] === undefined"/>
                <btn-tab tab-id="thursday" key="thursday" :label="'Jeudi ' + formatDate(search.week.days['thursday'])"  :disabled="planning[search.week.days['thursday']] === undefined"/>
                <btn-tab tab-id="friday" key="friday" :label="'Vendredi ' + formatDate(search.week.days['friday'])"  :disabled="planning[search.week.days['friday']] === undefined"/>
                <btn-tab tab-id="saturday" key="saturday" :label="'Samedi ' + formatDate(search.week.days['saturday'])"  :disabled="planning[search.week.days['saturday']] === undefined"/>
                <btn-tab tab-id="sunday" key="sunday" :label="'Dimanche ' + formatDate(search.week.days['sunday'])"  :disabled="planning[search.week.days['sunday']] === undefined"/>
            </ul>
            <div class="tab-content" id="tab-planning-content">
                <div v-if="planningLoading" class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <planning-tab-sanctuary v-if="planning[search.week.days['monday']] !== undefined" :planning="planning[search.week.days['monday']]" :my-sessions="mySessions" :establishments="search.establishments" :refs="refs" :date="search.week.days.monday" tab-id="monday" key="monday"/>
                <planning-tab-sanctuary v-if="planning[search.week.days['tuesday']] !== undefined" :planning="planning[search.week.days['tuesday']]" :my-sessions="mySessions" :establishments="search.establishments" :refs="refs" :date="search.week.days.tuesday" tab-id="tuesday" key="tuesday" />
                <planning-tab-sanctuary v-if="planning[search.week.days['wednesday']] !== undefined" :planning="planning[search.week.days['wednesday']]" :my-sessions="mySessions" :establishments="search.establishments" :refs="refs" :date="search.week.days.wednesday" tab-id="wednesday" key="wednesday" />
                <planning-tab-sanctuary v-if="planning[search.week.days['thursday']] !== undefined" :planning="planning[search.week.days['thursday']]" :my-sessions="mySessions" :establishments="search.establishments" :refs="refs" :date="search.week.days.thursday" tab-id="thursday" key="thursday" />
                <planning-tab-sanctuary v-if="planning[search.week.days['friday']] !== undefined" :planning="planning[search.week.days['friday']]" :my-sessions="mySessions" :establishments="search.establishments" :refs="refs" :date="search.week.days.friday" tab-id="friday" key="friday" />
                <planning-tab-sanctuary v-if="planning[search.week.days['saturday']] !== undefined" :planning="planning[search.week.days['saturday']]" :my-sessions="mySessions" :establishments="search.establishments" :refs="refs" :date="search.week.days.saturday" tab-id="saturday" key="saturday" />
                <planning-tab-sanctuary v-if="planning[search.week.days['sunday']] !== undefined" :planning="planning[search.week.days['sunday']]" :my-sessions="mySessions" :establishments="search.establishments" :refs="refs" :date="search.week.days.sunday" tab-id="sunday" key="sunday" />
            </div>
        </div>
    </main>
</div>
</template>

<style scoped>
header {
    background: #000;
    text-align: center;
    padding: 15px
}

#tab-planning {
    border: none;
    margin-bottom: 2rem;
    gap: 0.5rem;
    padding: 0.5rem;
    background: #f9fafb;
    border-radius: 16px;
}

:deep(.nav-link) {
    border: none !important;
    color: #9ca3af !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.1em !important;
    font-size: 0.75rem !important;
    padding: 1.25rem 0.5rem !important;
    position: relative;
    transition: all 0.2s ease !important;
    background: transparent !important;
}

:deep(.nav-link:hover:not(:disabled)) {
    color: #111827 !important;
}

:deep(.nav-link.active) {
    color: #fff !important;
    background-color: #000 !important;
    font-weight: 900 !important;
    border-radius: 50px !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

:deep(.nav-link:disabled) {
    opacity: 0.2;
    cursor: not-allowed;
}

@media (max-width: 768px) {
    #tab-planning {
        gap: 0;
    }
    :deep(.nav-link) {
        font-size: 0.65rem !important;
        padding: 1rem 0.25rem !important;
        letter-spacing: 0.05em !important;
    }
}
</style>
